import {
  ComponentPropsContext,
  SitecoreContext,
  StaticPath,
} from '@sitecore-jss/sitecore-jss-nextjs'
import { GetStaticPaths, GetStaticProps } from 'next'

import { JobData } from 'components/front-end/JobDetails/_JobInterface'
import { SitecorePageProps } from 'lib/page-props'
import { sitecorePagePropsFactory } from 'lib/page-props-factory'
import { componentFactory } from 'temp/componentFactory'
import Layout from 'src/Layout'

interface JobPageProps extends SitecorePageProps {
  jobData: JobData
}

const JobPage = ({ componentProps, layoutData, jobData }: JobPageProps): JSX.Element => {
  return (
    <ComponentPropsContext value={componentProps}>
      <SitecoreContext
        componentFactory={componentFactory}
        layoutData={layoutData}
      >
        {/*
          Sitecore Pages supports component rendering to avoid refreshing the entire page during component editing.
          If you are using Experience Editor only, this logic can be removed, Layout can be left.
        */}
        <Layout layoutData={layoutData} jobData={jobData} />
      </SitecoreContext>
    </ComponentPropsContext>
  )
}

// This function gets called at build and export time to determine
// pages for SSG ("paths", as tokenized array).
export const getStaticPaths: GetStaticPaths = async () => {
  // Fallback, along with revalidate in getStaticProps (below),
  // enables Incremental Static Regeneration. This allows us to
  // leave certain (or all) paths empty if desired and static pages
  // will be generated on request (development mode in this example).
  // Alternatively, the entire sitemap could be pre-rendered
  // ahead of time (non-development mode in this example).
  // See https://nextjs.org/docs/basic-features/data-fetching/incremental-static-regeneration

  const paths: StaticPath[] = []
  const fallback: boolean | 'blocking' = 'blocking'

  return {
    paths,
    fallback,
  }
}

// This function gets called at build time on server-side.
// It may be called again, on a serverless function, if
// revalidation (or fallback) is enabled and a new request comes in.
export const getStaticProps: GetStaticProps = async (context) => {
  const path1 = context.params?.path?.[0]
  const path2 = context.params?.path?.[1]
  const path3 = context.params?.path?.[2]

  const isReserve = path1 == 'reserves'
  const service = isReserve ? path2 : path1
  const job = isReserve ? path3 : path2

  try {
    const responseAsync = fetch(`${process.env.SITECORE_API_HOST}/api/v2/jobs/JobDetailsSearch`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ name: job, service, isReserve }),
    })

    context.params = { path: ['jobs', 'job-details'] }

    const propsAsync = sitecorePagePropsFactory.create(context) as Promise<JobPageProps>

    const [response, props] = await Promise.all([responseAsync, propsAsync])
    if (response.status === 200) {
      props.jobData = await response.json()
    }

    return {
      props,
      // Next.js will attempt to re-generate the page:
      // - When a request comes in
      // - At most once every 5 seconds
      revalidate: 5, // In seconds
      notFound: response.status !== 200,
    }
  } catch (error) {
    console.error(error)
  }
  return {
    props: {},
    // Next.js will attempt to re-generate the page:
    // - When a request comes in
    // - At most once every 5 seconds
    revalidate: 1, // In seconds
    notFound: true,
  }
}

export default JobPage
